<template>
    <template v-if="joinMode === JoinModeEnum.Blocked">
        <Notification class="is-warning mt-4" :centered="false">
            <span class="icon">
                <i class="fas fa-exclamation-triangle"></i>
            </span>
            <span>
                <strong>{{ translate("academy_modal.rejected_blocked_title") }}</strong>
                {{ translate("academy_modal.rejected_blocked_explanation") }}
            </span>
        </Notification>
    </template>
    <template v-else>
        <p class="has-text-grey-light mt-3 mb-3">
            {{
                translate(
                    joinMode === JoinModeEnum.Mandatory
                        ? `${keyPrefix}.join_academy_mandatory_explanation`
                        : `${keyPrefix}.join_academy_explanation`,
                )
            }}
        </p>

        <CheckboxRow
            v-if="showCheckbox"
            :model-value="modelValue"
            :emphasized="joinMode === JoinModeEnum.Mandatory"
            :tag-label="joinMode === JoinModeEnum.Mandatory ? translate('academy_modal.required_badge') : undefined"
            @update:model-value="emit('update:modelValue', $event)"
        >
            {{ translate("academy_modal.join_academy_label") }}
            <span v-if="joinMode === JoinModeEnum.Mandatory" class="has-text-primary ml-1">*</span>
        </CheckboxRow>

        <Notification v-if="showVisibilityNotice" class="is-info mt-4" :centered="false">
            <span class="icon">
                <i class="fas fa-info-circle"></i>
            </span>
            <span>
                <strong>{{ translate(`${keyPrefix}.join_academy_visibility_title`) }}</strong>
                {{ translate(`${keyPrefix}.join_academy_visibility_explanation`) }}
            </span>
        </Notification>
    </template>
</template>

<script setup lang="ts">
    import { translate } from "../Common/translations";
    import Notification from "../Common/Notification.vue";
    import CheckboxRow from "../Common/CheckboxRow.vue";
    import { JoinMode, JoinModeEnum } from "../Api/academies";

    withDefaults(
        defineProps<{
            joinMode: JoinMode;
            modelValue: boolean;
            /** Whether the disclosure shows its opt-in/acknowledgment checkbox at all, regardless
             * of join mode. The modal's original behaviour always shows it (Mandatory's checkbox
             * is a real gate there, since representing a manually-approved academy is mandatory to
             * join during registration). A dedicated join entry point (the Browse academies page)
             * has no registration context, so the checkbox would be a redundant extra click there
             * — it sets `showCheckbox` to `false` for both Mandatory and Optional modes. */
            showCheckbox?: boolean;
            /** Translation key namespace for the explanation/visibility copy, so a caller with a
             * different context (e.g. the Browse academies page, which isn't a registration flow)
             * can swap in its own wording without affecting the modal's original keys. */
            keyPrefix?: string;
            /** Whether the "Visible to Managers" info notification renders at all. The Browse
             * academies page's Mandatory-mode explanation already states the PII-visibility fact
             * inline, so repeating it in a separate notification is redundant there. */
            showVisibilityNotice?: boolean;
        }>(),
        {
            showCheckbox: true,
            keyPrefix: "academy_modal",
            showVisibilityNotice: true,
        },
    );

    const emit = defineEmits<{
        "update:modelValue": [boolean];
    }>();
</script>
